import { useState, useCallback } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';

const api = axios.create({
  baseURL: 'https://sist.sintrabor.org.br/intranet-backend/api/v1',
  timeout: 60000,
});

function buildQueryString(params) {
  if (!params || Object.keys(params).length === 0) return '';
  
  const query = Object.entries(params)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
  
  return query ? `?${query}` : '';
}

const useApiIntranet = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  
  // Estados separados para cada tipo de dado
  const [situacoes, setSituacoes] = useState([]);
  const [trabalhadores, setTrabalhadores] = useState([]);
  const [areas, setAreas] = useState([]);
  const [regionais, setRegionais] = useState([]);
  
  const token = useSelector((state) => state.login.token);

  const getHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
  }), [token]);

  const resetState = () => {
    setIsError(false);
    setError(null);
  };

  // ========================================
  // PROCESSOS (Jurídico)
  // ========================================
  
  const getProcessos = useCallback((filtros = {}) => {
    resetState();
    setIsLoading(true);
    
    api.get(`/processos${buildQueryString(filtros)}`, { headers: getHeaders() })
      .then((response) => {
        setData(response.data);
      })
      .catch((err) => {
        setIsError(true);
        setError(err.response?.data?.message || 'Erro ao buscar processos');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [getHeaders]);

  // ========================================
  // SITUAÇÕES DE PROCESSO
  // ========================================
  
  const getSituacoesProcesso = useCallback(() => {
    resetState();
    
    api.get('/situacoes-processo', { headers: getHeaders() })
      .then((response) => {
        if (response.data?.data) {
          setSituacoes(response.data.data);
        }
      })
      .catch((err) => {
        setIsError(true);
        setError(err.response?.data?.message || 'Erro ao buscar situações');
        setSituacoes([]);
      });
  }, [getHeaders]);

  // ========================================
  // TRABALHADORES JURÍDICOS
  // ========================================
  
  const getTrabalhadorJuridicos = useCallback(() => {
    resetState();
    
    api.get('/trabalhadores-juridicos', { headers: getHeaders() })
      .then((response) => {
        if (response.data?.data) {
          setTrabalhadores(response.data.data);
        }
      })
      .catch((err) => {
        setIsError(true);
        setError(err.response?.data?.message || 'Erro ao buscar trabalhadores jurídicos');
        setTrabalhadores([]);
      });
  }, [getHeaders]);

  // ========================================
  // ÁREAS DE DIREITO
  // ========================================
  
  const getAreasDireito = useCallback(() => {
    resetState();
    
    api.get('/areas-direito', { headers: getHeaders() })
      .then((response) => {
        if (response.data?.data) {
          setAreas(response.data.data);
        }
      })
      .catch((err) => {
        setIsError(true);
        setError(err.response?.data?.message || 'Erro ao buscar áreas de direito');
        setAreas([]);
      });
  }, [getHeaders]);

  // ========================================
  // REGIONAIS
  // ========================================
  const getRegionaisProcessos = useCallback(() => {
    resetState();

    api.get('/regionais', { headers: getHeaders() })
      .then((response) => {
        const payload = response.data?.data ?? response.data ?? [];
        setRegionais(Array.isArray(payload) ? payload : []);
      })
      .catch((err) => {
        setIsError(true);
        setError(err.response?.data?.message || 'Erro ao buscar regionais');
        setRegionais([]);
      });
  }, [getHeaders]);

  return {
    // Estado
    isLoading,
    isError,
    error,
    data,
    
    // Estados separados
    situacoes,
    trabalhadores,
    areas,
    regionais,
    
    // Processos
    getProcessos,
    
    // Juridico
    getSituacoesProcesso,
    getTrabalhadorJuridicos,
    getAreasDireito,
    getRegionaisProcessos,
  };
};

export default useApiIntranet;
